/*
  Pure helpers for the Club<->Agent chat lifecycle timer.

  The backend is the source of truth. `GET /dashboard/chat/timer-status/` returns
  a status object (see timer-builder contract) whose `can_extend` / `can_revive`
  / `is_club` fields decide the controls — the UI mirrors those, it never guesses
  from roles. The chip countdown is driven off `remaining_seconds` (anchored at
  fetch time to survive client-clock skew) with `channel.data.timer_expires_at`
  as an optional zero-fetch display seed.

  These helpers stay free of React / Stream so they can be unit-tested and reused
  by the chip, the composer banner and the dev harness.
*/

export const DEFAULT_INITIAL_DAYS = 5;
export const DEFAULT_EXTENSION_CHOICES = [5, 10, 14];

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

// Time formatting phrases. Kept as whole English strings (number rendered
// separately by the UI) so the static translator matches them by key and never
// has to reassemble a number with a label.
export const TIMER_PHRASES = {
  DAY_LEFT: "day left",
  DAYS_LEFT: "days left",
  HOUR_LEFT: "hour left",
  HOURS_LEFT: "hours left",
  UNDER_HOUR: "Less than an hour left",
  EXPIRED: "Expired",
};

// Stable backend error identifiers mapped to translatable copy. `chat_expired`
// is defensive (the UI already hides extend once expired); it reuses the
// existing expired-banner string so it is already translated.
export const TIMER_ERROR_COPY = {
  invalid_duration: "That extension length is not available.",
  not_expired: "This conversation is still active.",
  chat_expired: "This conversation has expired.",
  // Reactivate was called on a timer that isn't deactivated (T034 / BE #176):
  // an active timer is extended, an expired one revived.
  not_deactivated: "This conversation's timer is already running.",
};

const toMs = (value) => {
  if (value === undefined || value === null || value === "") return null;
  if (typeof value === "number") {
    return value < 1e12 ? value * 1000 : value;
  }
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
};

/*
  Strip the Stream channel type prefix ("messaging:") to get the `channel_cid`
  the timer endpoints expect. Accepts a full cid or a bare id.
*/
export const stripMessagingPrefix = (cid) => {
  if (!cid) return null;
  const str = String(cid);
  const idx = str.indexOf(":");
  return idx >= 0 ? str.slice(idx + 1) : str;
};

const isClubAgentContext = (context) =>
  typeof context === "string" && /club[_-]?agent/i.test(context);

// True for the agent↔agent 14d window born from an accepted Last Minute request.
// The timed agent_agent chat is recognised by its stamped `timer_expires_at`
// (hasTimerHint) — NOT by context, because ordinary agent_agent chats stay untimed
// (DECISIONS D-007). This helper is only for LABEL branching (M4-F3): the chip must
// read neutral for agent_agent and NEVER derive club wording from is_club, which the
// BE reports true for the accepter (they sit in the controller slot).
export const isAgentAgentContext = (context) =>
  typeof context === "string" && /agent[_-]?agent/i.test(context);

/*
  Cheap signal (from Stream channel data) for whether a chat MIGHT carry a timer,
  used to decide whether to hit the status endpoint at all. Active timer chats
  carry a stamped `timer_expires_at`; expired ones are frozen. Untimed active
  chats have neither, so we skip the request (and its 404) for them.
*/
export const hasTimerHint = (channelData) => {
  const data = channelData || {};
  const ctx = data._tp_custom_context || {};
  const expires =
    data.timer_expires_at ?? ctx.timer_expires_at ?? data.expires_at ?? ctx.expires_at;
  if (expires !== undefined && expires !== null) return true;
  if (data.frozen === true) return true;
  return isClubAgentContext(data.tp_context) || isClubAgentContext(ctx.tp_context);
};

/*
  Read an embedded status object off channel data, if present. The backend is
  not required to stamp the full object (it only guarantees `timer_expires_at`),
  but supporting it gives a zero-fetch path and lets the dev harness render the
  real components without a backend.
*/
export const readEmbeddedStatus = (channelData) => {
  const data = channelData || {};
  const embedded =
    data._tp_timer || (data._tp_custom_context && data._tp_custom_context._tp_timer) || null;
  return embedded && typeof embedded === "object" ? embedded : null;
};

/*
  Normalize a timer-status API object into the UI-ready shape. `fetchedAtMs`
  anchors `remaining_seconds` to an absolute deadline so the local countdown
  tracks the server's remaining time rather than the client's wall clock.
*/
export const normalizeTimerStatus = (api, fetchedAtMs = Date.now()) => {
  const obj = api || {};
  const isExpired = obj.is_expired === true || obj.status === "expired";

  let expiresAtMs = toMs(obj.expires_at);
  if (
    expiresAtMs === null &&
    typeof obj.remaining_seconds === "number" &&
    Number.isFinite(obj.remaining_seconds) &&
    obj.remaining_seconds > 0
  ) {
    expiresAtMs = fetchedAtMs + obj.remaining_seconds * 1000;
  }

  const extensionChoices =
    Array.isArray(obj.extension_choices) && obj.extension_choices.length
      ? obj.extension_choices
      : DEFAULT_EXTENSION_CHOICES;

  return {
    isTimerChat: true,
    context: obj.context || null,
    status: obj.status || (isExpired ? "expired" : "active"),
    isExpired,
    isClub: obj.is_club === true,
    canExtend: obj.can_extend === true,
    canRevive: obj.can_revive === true,
    expiresAtMs,
    remainingSeconds:
      typeof obj.remaining_seconds === "number" ? obj.remaining_seconds : null,
    extensionChoices,
    controlsAuthoritative: true,
  };
};

/*
  Display-only fallback built from Stream channel data, used while the status
  fetch is in flight or if it errors (not a 404). Shows the chip but NEVER the
  club controls — we don't render extend/revive without an authoritative gate.
*/
export const seedFromChannelData = (channelData) => {
  const data = channelData || {};
  const ctx = data._tp_custom_context || {};
  const expiresAtMs = toMs(
    data.timer_expires_at ?? ctx.timer_expires_at ?? data.expires_at ?? ctx.expires_at
  );
  // Prefer the stamped timer_status: it distinguishes a TIMER expiry from a
  // manual close (both set frozen=true). A manual close keeps timer_status
  // "active", so we must not seed it as expired. Fall back to frozen/deadline
  // only when no status is stamped.
  const timerStatus = data.timer_status ?? ctx.timer_status ?? null;
  const isExpired =
    timerStatus === "expired" ||
    (timerStatus == null &&
      (data.frozen === true || (expiresAtMs !== null && expiresAtMs <= Date.now())));
  return {
    isTimerChat: true,
    context: ctx.tp_context || data.tp_context || null,
    status: isExpired ? "expired" : "active",
    isExpired,
    isClub: false,
    canExtend: false,
    canRevive: false,
    expiresAtMs,
    remainingSeconds: null,
    extensionChoices: DEFAULT_EXTENSION_CHOICES,
    controlsAuthoritative: false,
  };
};

/*
  Turn a remaining duration into a calm single-largest-unit display. The number
  and its label are returned separately (`count` + `phrase`) so the UI can keep
  the digit out of the translator's reach. `urgent` drives the amber styling
  (anything under a day, per spec).
*/
export const formatRemaining = (expiresAtMs, nowMs = Date.now()) => {
  if (expiresAtMs === null || expiresAtMs === undefined) {
    return { count: null, phrase: TIMER_PHRASES.EXPIRED, urgent: false, expired: true };
  }

  const diff = expiresAtMs - nowMs;
  if (diff <= 0) {
    return { count: null, phrase: TIMER_PHRASES.EXPIRED, urgent: true, expired: true };
  }

  // T009: the UNIT is still chosen by whole days (so anything under a day keeps
  // falling through to the calm hours/under-an-hour branches), but the DISPLAYED
  // count is rounded, not floored. The server sets expiry to start + exactly N
  // days, so by the time the client renders, `diff` is a hair under N and a
  // floor turned the promised "5-day chat" into "Expires in 4 days" — the user
  // never once saw the number they were promised. Rounding shows N from the
  // first render and drifts by at most half a day thereafter; ceil was rejected
  // because it would make TIMER_PHRASES.DAY_LEFT unreachable (a 1d-2d remainder
  // would read "2 days") and would overstate by up to a full day.
  const wholeDays = Math.floor(diff / DAY_MS);
  if (wholeDays >= 1) {
    const days = Math.round(diff / DAY_MS);
    return {
      count: days,
      phrase: days === 1 ? TIMER_PHRASES.DAY_LEFT : TIMER_PHRASES.DAYS_LEFT,
      urgent: false,
      expired: false,
    };
  }

  const hours = Math.floor(diff / HOUR_MS);
  if (hours >= 1) {
    return {
      count: hours,
      phrase: hours === 1 ? TIMER_PHRASES.HOUR_LEFT : TIMER_PHRASES.HOURS_LEFT,
      urgent: true,
      expired: false,
    };
  }

  return { count: null, phrase: TIMER_PHRASES.UNDER_HOUR, urgent: true, expired: false };
};

// Expired chats always read as "Expired" regardless of any stale deadline.
export const remainingForStatus = (status, nowMs = Date.now()) => {
  if (!status) return null;
  if (status.isExpired) {
    return { count: null, phrase: TIMER_PHRASES.EXPIRED, urgent: false, expired: true };
  }
  return formatRemaining(status.expiresAtMs, nowMs);
};
